import { Dispatch, SetStateAction, useCallback, useState } from "react";

import isFunction from "lodash-es/isFunction";

import { edit, redo, reset, undo, undoAll } from "@app/util/undo";
import EditorState from "@app/util/undo/EditorState";

import { AddItemsCommand, RemoveItemsCommand } from "./commands";

const identityKey = <T>(item: T): T => item;

const useUndoableListState = <T>(
  initialValue: T[],
  getKey: (item: T) => unknown = identityKey
): [
  value: T[],
  setValue: Dispatch<SetStateAction<T[]>>,
  valueState: {
    canRedo: boolean;
    canUndo: boolean;
    hasChanges: boolean;
  },
  actions: {
    redo: () => void;
    reset: (value: T[]) => void;
    undo: () => void;
    undoAll: () => void;
  }
] => {
  const [state, setState] = useState<EditorState<T[]>>(reset(initialValue));

  const setValue: Dispatch<SetStateAction<T[]>> = useCallback(
    (param: ((previousValue: T[]) => T[]) | T[]) => {
      setState((state) => {
        const newValue = isFunction(param) ? param(state.data) : param;
        const keysBefore = new Map(state.data.map((item) => [getKey(item), item]));
        const keysAfter = new Map(newValue.map((item) => [getKey(item), item]));
        const addedItems: T[] = [];
        const removedItems: T[] = [];
        for (const [key, item] of keysAfter) {
          if (!keysBefore.has(key)) {
            addedItems.push(item);
          }
        }
        for (const [key, item] of keysBefore) {
          if (!keysAfter.has(key)) {
            removedItems.push(item);
          }
        }
        if (addedItems.length) {
          state = edit(state, new AddItemsCommand(addedItems, getKey));
        }
        if (removedItems.length) {
          state = edit(state, new RemoveItemsCommand(removedItems, getKey));
        }
        return state;
      });
    },
    [getKey]
  );

  const handleUndo = useCallback(() => setState(undo), []);
  const handleRedo = useCallback(() => setState(redo), []);
  const handleUndoAll = useCallback(() => setState(undoAll), []);
  const handleReset = useCallback((value: T[]) => setState(reset(value)), []);

  return [
    state.data,
    setValue,
    {
      canRedo: state.revertedChanges.size > 0,
      canUndo: state.appliedCommands.size > 0,
      hasChanges: state.hasChanges,
    },
    {
      redo: handleRedo,
      reset: handleReset,
      undo: handleUndo,
      undoAll: handleUndoAll,
    },
  ];
};

export default useUndoableListState;
